import { useMemo, useState } from 'react'
import { driverName, enteredDrivers } from './screenContext'
import { formatClock } from '@racedash/shared'
import { Alert, Button, Group, Select, Text } from '@mantine/core'
import {
  findCar,
  nextDriver,
  resolveDriver,
  stintVendorDriver,
  vendorDriverName,
} from '@racedash/shared'
import type { ScreenContext } from './screenContext'
import type { DashboardData } from './useSessionData'

/**
 * Naming the driver who has just taken over.
 *
 * The stint-count rule needs every stint attributed, and a race is a dozen
 * stops — so this only works if saying who got in is close to free. The plan
 * already knows who is due, which turns the whole thing into one tap.
 *
 * It appears when a stop has completed and nobody has said who came out of it.
 * Nothing is written until somebody confirms: guessing from the rotation would
 * defeat the point, because the rotation is exactly what gets departed from
 * when it matters.
 *
 * And not when the timing system has already answered. Some circuits chip the
 * drivers, so the feed names the new one itself — see vendorHasAnswered.
 */
export default function ConfirmStop({
  car,
  data,
  now,
  context,
}: {
  car: string | null
  data: DashboardData
  now: Date
  context: ScreenContext
}) {
  const [dismissed, setDismissed] = useState<string | null>(null)
  const [choice, setChoice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const pending = useMemo(() => {
    if (!car || !context.declare) return null

    // The most recent completed stop for this car.
    const stops = data.pitStops
      .filter((s) => s.car === car && s.completedAt)
      .sort((a, b) => (a.completedAt?.toMillis() ?? 0) - (b.completedAt?.toMillis() ?? 0))
    const last = stops.at(-1)
    if (!last?.completedAt) return null

    const out = last.completedAt.toDate()

    // Only while it is still current. A stop from three hours ago is history,
    // not a question — and the stint chart is where history gets corrected.
    if (now.getTime() - out.getTime() > staleAfterMs) return null

    // Already answered: somebody declared at or after this pit-out.
    const declared = resolveDriver(context.declarations, car, out)
    if (declared.source === 'team') return null

    // Or the timing system answered it for us.
    if (vendorHasAnswered(data, car, out)) return null

    return { stop: last, out }
  }, [car, data, context.declare, context.declarations, now])

  if (!pending || !car) return null

  const key = `${pending.stop.stopNumber}`
  if (dismissed === key) return null

  // The same narrowing the stint chart does: the people entered in this car,
  // not everybody who has ever driven for the team.
  const choices = enteredDrivers(context, car)

  const rotation = context.plan?.drivers ?? []
  // Who was in before this stop, so the rotation can say who is next.
  const before = resolveDriver(context.declarations, car, new Date(pending.out.getTime() - 1))
  const suggested = nextDriver(rotation, before.source === 'team' ? before.driverId : null)
  const picked = choice ?? suggested
  const nameOf = (id: string | null) => driverName(context, id)

  const confirm = async () => {
    if (!picked) return
    setBusy(true)
    try {
      // Stamped at the pit-out, which is where the driver stint begins. Not at
      // the moment of tapping, which may be several laps later.
      await context.declare?.(car, picked, pending.out)
      setDismissed(key)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Alert color="blue" variant="light" mb="sm">
      <Group gap="sm" wrap="wrap" align="center">
        <Text size="sm">
          Car {car} stopped at{' '}
          {formatClock(pending.out)}. Who
          got in?
        </Text>
        <Select
          size="xs"
          w={190}
          placeholder="Pick a driver"
          data={choices.map((d) => ({ value: d.id, label: d.name }))}
          value={picked}
          onChange={setChoice}
          disabled={choices.length === 0}
          searchable
        />
        <Button size="compact-sm" loading={busy} disabled={!picked} onClick={() => void confirm()}>
          {picked === suggested && nameOf(suggested) ? `Confirm ${nameOf(suggested)}` : 'Confirm'}
        </Button>
        <Button
          size="compact-sm"
          variant="subtle"
          color="gray"
          onClick={() => {
            // Dismissing leaves the stint unattributed on purpose, and the
            // budget widget will say so rather than pretending otherwise.
            setDismissed(key)
          }}
        >
          Not now
        </Button>
      </Group>
    </Alert>
  )
}

/**
 * How long a stop stays worth asking about.
 *
 * Long enough to cover a lap or two of not looking at the screen, short enough
 * that opening the dashboard hours later does not present a question about a
 * stop nobody remembers.
 */
const staleAfterMs = 15 * 60_000

/**
 * Whether the timing system is the one answering this question.
 *
 * Some circuits chip the drivers, and there the feed names the new one as the
 * car leaves the pit lane. Asking then is asking somebody to retype what is
 * already on the screen in front of them.
 *
 * The trap is that a feed which does NOT track driver changes still carries a
 * driver — whoever started the stint that has just ended, because nothing told
 * it otherwise. So the presence of a name proves nothing. A name that CHANGES
 * proves everything: only something at the circuit could have caused it.
 *
 * Two ways to see it, because one alone is not enough. Across this stop, from
 * the standings against the last lap before the car came in, which catches the
 * first change of the session the moment it happens. And anywhere this session,
 * from the stints the interpreter has already materialised, which is what
 * covers the stops where the same driver gets back in — at Zandvoort barely
 * half the stops were a change, and on the per-stop test alone the other half
 * would still be asking a question the feed had already shown it could answer.
 *
 * Best effort, deliberately. Standings, laps and stints are loaded only if a
 * screen on this dashboard asked for them, exactly as the pit stops this whole
 * component runs on are. Unable to tell reads as not knowing, and prompts,
 * which is the behaviour this has always had.
 */
function vendorHasAnswered(data: DashboardData, car: string, out: Date): boolean {
  return changedAcrossThisStop(data, car, out) || changedAnywhereThisSession(data)
}

function changedAcrossThisStop(data: DashboardData, car: string, out: Date): boolean {
  const now = vendorDriverName(findCar(data.standings, car)?.driver)
  if (!now) return false

  // The last lap the car completed before it came in. Laps arrive newest first.
  const before = data.laps.find(
    (l) => l.car === car && (l.completedAt?.toMillis() ?? 0) <= out.getTime(),
  )
  const was = vendorDriverName(before?.driver)
  return !!was && now !== was
}

/**
 * Has this feed named two different drivers in one car, at any point today?
 *
 * Any car, not just ours: it is a question about the timing installation rather
 * than about us, and the answer arrives sooner from thirty-eight cars than
 * from one.
 */
function changedAnywhereThisSession(data: DashboardData): boolean {
  return data.stints.some((doc) => {
    const names = (doc.stints ?? [])
      .map((s) => vendorDriverName(stintVendorDriver(s).name))
      .filter((n): n is string => n != null)
    return new Set(names).size > 1
  })
}
